import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { StreamPlayer } from "@/components/stream/StreamPlayer";
import { StreamComments } from "@/components/stream/StreamComments";
import { StreamHostPanel } from "@/components/stream/StreamHostPanel";
import { fmtDate, fmtTimeRange, fmtDateTime } from "@/lib/format";

const LOCK_COPY = {
  sign_in: { t: "Sign in to watch", b: "This stream is for ticket holders and members.", cta: "Sign in", to: "/login" },
  ticket_required: { t: "A ticket is needed to watch", b: "Buy access to this broadcast, or upgrade your membership if it's included at a higher level. This page opens the stream as soon as you have access.", cta: "Get access", to: null, alt: { cta: "Upgrade membership", to: "/membership" } },
  program_required: { t: "This broadcast is part of a program", b: "It's included for members of the program's group. Join the program to watch.", cta: "Browse enhancers", to: "/enhancers" },
  removed: { t: "You no longer have access", b: "A host has removed access to this stream. Contact us if you think this is a mistake.", cta: "Contact us", to: "/contact" },
};

function Locked({ reason, eventPath }) {
  const c = LOCK_COPY[reason] || LOCK_COPY.ticket_required;
  return (
    <div className="aspect-video rounded-2xl bg-[#144F75] text-white grid place-items-center text-center px-6" data-testid="watch-locked">
      <div>
        <Lock className="mx-auto mb-3" size={30} />
        <p className="font-serif text-2xl" data-testid="watch-locked-title">{c.t}</p>
        <p className="text-white/80 mt-2 max-w-md">{c.b}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-5">
          <Link to={c.to || eventPath} data-testid="watch-locked-cta" className="eyw-btn !bg-white !text-[#144F75] inline-flex">{c.cta}</Link>
          {c.alt && <Link to={c.alt.to} data-testid="watch-locked-upgrade" className="eyw-btn-ghost !text-white !border-white/50 inline-flex">{c.alt.cta}</Link>}
        </div>
      </div>
    </div>
  );
}

const STATUS = { live: "LIVE NOW", scheduled: "Starting soon", ended: "Replay" };

export default function EventWatch() {
  const { id } = useParams();
  const qc = useQueryClient();
  const key = ["event-stream", id];
  const { data: s, isLoading, isError } = useQuery({ queryKey: key, queryFn: () => api.get(`/events/${id}/stream`).then((r) => r.data), refetchInterval: 20000, retry: false });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const canWatch = !!s?.can_watch;
  useEffect(() => { if (canWatch) api.post(`/programs/broadcasts/${id}/watched`).catch(() => {}); }, [canWatch, id]);

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]" data-testid="watch-loading"><Loader2 className="animate-spin" /></div>;
  if (isError || !s) return (
    <div className="min-h-[60vh] grid place-items-center text-center px-6" data-testid="watch-error">
      <div><h1 className="font-serif text-3xl text-[#1A242B] mb-2">Stream not found</h1><Link to="/events" className="eyw-btn !py-2 text-sm mt-4">Browse events</Link></div>
    </div>
  );
  const eventPath = `/events/${s.event.slug || s.event.id}`;
  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-10" data-testid="event-watch-page">
      <Seo title={`Watch: ${s.event.title}`} description={`Live stream and replay for ${s.event.title}.`} path={`${eventPath}/watch`}
        image={s.event.image || ""} noindex={s.watch_access !== "public"}
        jsonLd={s.status === "ended" && s.replay_available ? {
          "@context": "https://schema.org", "@type": "VideoObject", name: `${s.event.title} (replay)`,
          description: `Replay of ${s.event.title} from Enhancing Your World.`, uploadDate: `${s.event.date}T${s.event.start_time || "00:00"}:00`,
          thumbnailUrl: s.event.image || undefined, url: `${window.location.origin}${eventPath}/watch`,
        } : {
          "@context": "https://schema.org", "@type": "Event", name: s.event.title, startDate: `${s.event.date}T${s.event.start_time || "00:00"}:00`,
          eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode", eventStatus: "https://schema.org/EventScheduled",
          location: { "@type": "VirtualLocation", url: `${window.location.origin}${eventPath}/watch` }, image: s.event.image || undefined,
          organizer: { "@type": "Organization", name: "Enhancing Your World", url: window.location.origin },
        }} />
      <Link to={eventPath} className="text-sm text-[#144F75] inline-flex items-center gap-1 mb-5 hover:underline" data-testid="watch-back"><ArrowLeft size={14} /> Event details</Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${s.status === "live" ? "bg-red-600 text-white" : "bg-[#144F75]/10 text-[#144F75]"}`} data-testid="watch-status">{STATUS[s.status]}</span>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1A242B]" data-testid="watch-title">{s.event.title}</h1>
        <span className="text-sm text-[#4A5D6B]">{fmtDate(s.event.date)} · {fmtTimeRange(s.event.start_time, s.event.end_time)}</span>
      </div>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          {s.can_watch ? <StreamPlayer s={s} /> : <Locked reason={s.reason} eventPath={eventPath} />}
          {s.status === "ended" && s.replay_available && <p className="text-xs text-[#8FA89B]" data-testid="watch-replay-note">Replay available until {fmtDateTime(s.replay_expires_at)}.</p>}
          {s.is_host && <StreamHostPanel eid={s.event.id} s={s} refresh={refresh} />}
        </div>
        {s.can_watch && <StreamComments eid={s.event.id} isHost={s.is_host} />}
      </div>
    </div>
  );
}
